import { useEffect, useRef } from 'react';
import { Alert } from 'react-native';
import { socket } from '@common/services/featherIO/feathers.io';

// Show the offline alert only after the connection has been down for this long,
// so brief transient reconnects don't trigger a false alarm.
const OFFLINE_ALERT_DELAY = 3000;

const OFFLINE_TITLE = '';
const OFFLINE_MESSAGE =
  'インターネットに接続できません。通信環境をご確認のうえ、再度お試しください。';

/**
 * Watches the socket.io connection to the backend and alerts the user when the
 * device loses network access (e.g. airplane mode). The app cannot function
 * without this connection, so socket connectivity is our source of truth for
 * "is there usable internet" — this avoids adding a native connectivity module.
 *
 * The alert is shown once per offline episode and resets when the socket
 * reconnects.
 */
const useNetworkStatus = (): void => {
  const hasAlertedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const showOfflineAlert = () => {
      if (hasAlertedRef.current) return;
      hasAlertedRef.current = true;
      Alert.alert(OFFLINE_TITLE, OFFLINE_MESSAGE, [{ text: 'OK' }]);
    };

    const handleDisconnected = () => {
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = undefined;
        if (!socket.connected) {
          showOfflineAlert();
        }
      }, OFFLINE_ALERT_DELAY);
    };

    const handleConnected = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      hasAlertedRef.current = false;
    };

    socket.on('connect_error', handleDisconnected);
    socket.on('connect_timeout', handleDisconnected);
    socket.on('disconnect', handleDisconnected);
    socket.on('connect', handleConnected);
    socket.on('reconnect', handleConnected);

    // Cover the case where the app is launched while already offline.
    if (!socket.connected) {
      handleDisconnected();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      socket.off('connect_error', handleDisconnected);
      socket.off('connect_timeout', handleDisconnected);
      socket.off('disconnect', handleDisconnected);
      socket.off('connect', handleConnected);
      socket.off('reconnect', handleConnected);
    };
  }, []);
};

export default useNetworkStatus;
